import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// Global axios interceptor — on any 401 from our API, try /auth/refresh once,
// then replay the original request. If the refresh also fails, show a clear
// session-expired toast so hosts don't get a bare "Not authenticated" error
// while mid-way through a listing form.
// -----------------------------------------------------------------------------
let isRefreshing = false;
let refreshQueue = [];
const flushQueue = (error) => {
  refreshQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
  refreshQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const url = original.url || '';
    const isApi = url.startsWith(API);
    const isAuthEndpoint =
      url.endsWith('/auth/refresh') || url.endsWith('/auth/login') || url.endsWith('/auth/logout');

    if (status !== 401 || !isApi || isAuthEndpoint || original._retry) {
      return Promise.reject(error);
    }
    original._retry = true;

    if (isRefreshing) {
      // Another refresh is in flight — queue up until it settles.
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: () => resolve(axios({ ...original, withCredentials: true })),
          reject,
        });
      });
    }

    isRefreshing = true;
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      flushQueue(null);
      isRefreshing = false;
      return axios({ ...original, withCredentials: true });
    } catch (refreshErr) {
      flushQueue(refreshErr);
      isRefreshing = false;
      // Only nag the user if this isn't the initial silent /auth/me probe.
      if (!url.endsWith('/auth/me')) {
        toast.error('Your session expired. Please sign in again.');
      }
      return Promise.reject(error);
    }
  }
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
      return detail
        .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name, role = 'guest') => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { email, password, name, role },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  const value = { user, loading, register, login, logout, checkAuth };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
